import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    UserProfile,
    UserProfileDefaultDescription,
} from '../../workers/ApiWorker';
import { useCustomTheme } from '../../contexts/ThemeContext';
import { useApi } from '../../contexts/ApiContext';
import UserAvatar from './UserAvatar';
import { createWorkerFactory, useWorker } from '@shopify/react-web-worker';
import { FOLLOW_USER_FAILED } from '../../helpers/Messages';

interface UserActionsProps {
    userProfiles: UserProfile[];
    closeModal?: (resetForm?: () => void) => void;
    setFollowers: (followers: number) => void;
    setFollowing: (following: number) => void;
    followers: number;
    following: number;
    isMobile: boolean;
}

const createApiWorker = createWorkerFactory(
    () => import('../../workers/ApiWorker')
);

const UserList: React.FC<UserActionsProps> = ({
    userProfiles,
    closeModal,
    setFollowers,
    setFollowing,
    followers,
    following,
    isMobile,
}) => {
    const { theme } = useCustomTheme();
    const { token } = useApi();
    const navigate = useNavigate();
    const apiWorker = useWorker(createApiWorker);

    function sanitizeQuillHtml(data: string) {
        let sanitizedHtml = data.replace(
            /<ol><li data-list="bullet">/g,
            '<ul><li>'
        );
        sanitizedHtml = sanitizedHtml.replace(/<\/li><\/ol>/g, '</li></ul>');
        sanitizedHtml = sanitizedHtml.replace(/\n/g, '<br />');
        return sanitizedHtml;
    }

    const [userFollowingMap, setUserFollowingMap] = useState<{
        [uuid: string]: boolean;
    }>({});

    useEffect(() => {
        const initialMap: { [uuid: string]: boolean } = {};
        userProfiles.forEach((profile) => {
            initialMap[profile.uuid] = profile.userFollowing ?? false;
        });
        setUserFollowingMap(initialMap);
    }, [userProfiles]);

    const handleClick = (username: string) => {
        setUserFollowingMap({});
        if (closeModal) closeModal();
        navigate(`/${username}`, {
            state: { fresh: true },
        });
    };

    return (
        <>
            {userProfiles.map((loadedUser) => {
                const isFollowing = userFollowingMap[loadedUser.uuid] ?? false;

                return (
                    <Box
                        key={loadedUser.uuid}
                        onClick={() => handleClick(loadedUser.username)}
                        sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: isMobile ? 'flex-start' : 'center',
                            justifyContent: 'space-between',
                            padding: 2,
                            gap: 1.5,
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                            },
                        }}
                    >
                        {/* Avatar + Info */}
                        <Box
                            sx={{
                                display: 'flex',
                                flex: 1,
                                alignItems: 'center',
                                gap: 2,
                                minWidth: 0,
                            }}
                        >
                            <UserAvatar
                                backup={loadedUser.username}
                                marginRight={2}
                                border={undefined}
                                userProfile={loadedUser}
                            />
                            <Box
                                sx={{
                                    maxWidth: isMobile
                                        ? '100%'
                                        : 'calc(100vw - 260px)',
                                    overflow: 'hidden',
                                    minWidth: 0,
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    color={theme.palette.text.primary}
                                    fontWeight="bold"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {loadedUser.username}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color={theme.palette.text.secondary}
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            sanitizeQuillHtml(
                                                loadedUser.description
                                            ) || UserProfileDefaultDescription,
                                    }}
                                    sx={{
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Follow/Unfollow Button */}
                        <Button
                            variant="contained"
                            size="small"
                            sx={{
                                whiteSpace: 'nowrap',
                                minWidth: isMobile ? '100%' : 90,
                                width: isMobile ? '100%' : 'auto', // <- This makes it expand
                                alignSelf: isMobile ? 'stretch' : 'center',
                                flexShrink: 0,
                            }}
                            onClick={async (e) => {
                                e.stopPropagation();
                                if (token && loadedUser) {
                                    try {
                                        const currentlyFollowing =
                                            userFollowingMap[loadedUser.uuid] ??
                                            false;

                                        await apiWorker.postUserProfileFollow(
                                            loadedUser.uuid,
                                            token
                                        );

                                        setUserFollowingMap((prev) => ({
                                            ...prev,
                                            [loadedUser.uuid]:
                                                !currentlyFollowing,
                                        }));

                                        if (isFollowing) {
                                            setFollowing(following - 1);
                                        } else {
                                            setFollowing(following + 1);
                                        }
                                    } catch (error) {
                                        console.warn(FOLLOW_USER_FAILED);
                                    }
                                }
                            }}
                        >
                            {isFollowing ? 'UNFOLLOW' : 'FOLLOW'}
                        </Button>
                    </Box>
                );
            })}
        </>
    );
};

export default UserList;
